import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  TagIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import { useDynamicPermissions } from '../../hooks/useDynamicPermissions';

interface OfferFormData {
  name: string;
  offerType: string;
  inactive: boolean;
  slaCategory: string;
  description: string;
}

interface Offer {
  id: string;
  name: string;
  offerType: string;
  inactive: boolean;
  slaCategory: string;
  description?: string;
}

const OfferEdit: React.FC = () => {
  const { can, loading: permissionsLoading } = useDynamicPermissions();
  const hasPermission = can('offers', 'update');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<OfferFormData>({
    name: '',
    offerType: '',
    inactive: false,
    slaCategory: 'Standard',
    description: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: offer, isLoading } = useQuery({
    queryKey: ['offer', id],
    queryFn: async () => {
      const response = await api.get(`/offers/${id}`);
      return response.data as Offer;
    },
    enabled: !!id,
  });

  // Get all offers for navigation
  const { data: offersData } = useQuery({
    queryKey: ['offers'],
    queryFn: async () => {
      const response = await api.get('/offers');
      return response.data;
    },
  });

  const { data: options } = useQuery({
    queryKey: ['offer-options'],
    queryFn: async () => {
      const response = await api.get('/offers/options');
      return response.data;
    },
  });

  // Initialize form data when offer is loaded
  useEffect(() => {
    if (offer) {
      setFormData({
        name: offer.name,
        offerType: offer.offerType,
        inactive: offer.inactive,
        slaCategory: offer.slaCategory,
        description: offer.description || ''
      });
    }
  }, [offer]);

  // Navigation logic
  const offers = offersData?.offers || [];
  const currentIndex = offers.findIndex((o: any) => o.id === id);
  const previousOffer = currentIndex > 0 ? offers[currentIndex - 1] : null;
  const nextOffer = currentIndex < offers.length - 1 ? offers[currentIndex + 1] : null;

  const navigateToOffer = (offerId: string) => {
    navigate(`/offers/${offerId}/edit`);
  };

  const updateMutation = useMutation({
    mutationFn: async (data: OfferFormData) => {
      const payload = {
        ...data,
        description: data.description || undefined
      };
      const response = await api.put(`/offers/${id}`, payload);
      return response.data;
    },
    onSuccess: (data) => {
      toast.success('Offer updated successfully');
      queryClient.setQueryData(['offer', id], data);
      queryClient.invalidateQueries({ queryKey: ['offers'] });
      navigate(`/offers/${id}`);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to update offer');
      if (error.response?.data?.details) {
        setErrors(error.response.data.details);
      }
    },
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.offerType) {
      newErrors.offerType = 'Offer type is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    updateMutation.mutate(formData);
  };

  const getOfferTypeLabel = (type: string) => {
    return type.replace('_', ' ');
  };

  // Show loading while checking permissions
  if (permissionsLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Check permission
  if (!hasPermission) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          You don't have permission to update offers.
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!offer) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-500 text-xl mb-4">Offer not found</div>
          <Link 
            to="/offers" 
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Back to Offers
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      {/* Header - exactly like Users page */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div className="sm:flex-auto">
          <div className="flex items-center space-x-3">
            <Link
              to="/offers"
              className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700"
            >
              <ArrowLeftIcon className="h-5 w-5 mr-1" />
              Back to Offers
            </Link>
          </div>
        </div>
        <div className="mt-4 sm:mt-0 sm:flex sm:space-x-3">
          <button
            onClick={handleSubmit}
            disabled={updateMutation.isPending}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {updateMutation.isPending ? 'Saving Changes' : 'Save Changes'}
          </button>
          <Link
            to={`/offers/${id}`}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Cancel
          </Link>
        </div>
      </div>

      {/* Offer Header with Icon and Title - exactly like Users page */}
      <div className="mt-6 flex items-center justify-between">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className="h-16 w-16 rounded-full bg-indigo-100 flex items-center justify-center">
              <TagIcon className="h-8 w-8 text-indigo-600" />
            </div>
          </div>
          <div className="ml-4">
            <h1 className="text-2xl font-bold text-gray-900">{offer.name}</h1>
            <p className="text-sm text-gray-500">{getOfferTypeLabel(offer.offerType)}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => previousOffer && navigateToOffer(previousOffer.id)}
            disabled={!previousOffer}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeftIcon className="h-5 w-5" />
          </button>
          <span className="text-sm text-gray-500">
            {currentIndex + 1} of {offers.length}
          </span>
          <button
            onClick={() => nextOffer && navigateToOffer(nextOffer.id)}
            disabled={!nextOffer}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRightIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Form - Labels and controls adjacent, proper row layout */}
      <div className="bg-white p-6 rounded-lg shadow mt-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Row 1: Name and Offer Type */}
          <div className="grid grid-cols-2 gap-8">
            <div className="flex items-center space-x-4">
              <label htmlFor="name" className="text-sm font-medium text-gray-700 w-20">
                Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={`flex-1 px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                  errors.name ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="Enter offer name"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name}</p>
              )}
            </div>

            <div className="flex items-center space-x-4">
              <label htmlFor="offerType" className="text-sm font-medium text-gray-700 w-24">
                Offer Type
              </label>
              <select
                id="offerType"
                name="offerType"
                value={formData.offerType}
                onChange={handleInputChange}
                className={`flex-1 px-3 py-1 border rounded text-sm h-8 focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                  errors.offerType ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                <option value="">Select offer type</option>
                {options?.offerTypes?.map((type: string) => (
                  <option key={type} value={type}>
                    {getOfferTypeLabel(type)}
                  </option>
                ))}
              </select>
              {errors.offerType && (
                <p className="mt-1 text-sm text-red-600">{errors.offerType}</p>
              )}
            </div>
          </div>

          {/* Row 2: SLA Category and Active checkbox */}
          <div className="grid grid-cols-2 gap-8">
            <div className="flex items-center space-x-4">
              <label htmlFor="slaCategory" className="text-sm font-medium text-gray-700 w-20">
                SLA Category
              </label>
              <input
                type="text"
                id="slaCategory"
                name="slaCategory"
                value={formData.slaCategory}
                readOnly
                className="flex-1 px-3 py-1 border rounded text-sm h-8 border-gray-300 bg-gray-100 text-gray-600"
              />
            </div>

            <div className="flex items-center space-x-4">
              <label className="text-sm font-medium text-gray-700 w-24">
                Active
              </label>
              <input
                id="active"
                name="active"
                type="checkbox"
                checked={!formData.inactive}
                onChange={(e) => setFormData(prev => ({ ...prev, inactive: !e.target.checked }))}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
            </div>
          </div>

          {/* Row 3: Description - full width */}
          <div className="flex items-start space-x-4">
            <label htmlFor="description" className="text-sm font-medium text-gray-700 w-20 pt-2">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={4}
              className="flex-1 px-3 py-1 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 border-gray-300"
              placeholder="Enter offer description"
            />
          </div>
        </form>
      </div>

      {/* Bottom pagination info - exactly like Users page */}
      <div className="mt-8 text-center">
        <p className="text-sm text-gray-500">
          Showing offer {currentIndex + 1} of {offers.length}
        </p>
      </div>
    </div>
  );
};

export default OfferEdit;